'use client';

import {
  type Dispatch,
  type MutableRefObject,
  type SetStateAction,
  createContext,
  useEffect,
  useRef,
  useState,
} from 'react';

export interface IProductSearchBoxCtx {
  open(): void;
  close(): void;
  focus(): void;
  reset(): void;
  modal: MutableRefObject<null>;
  dialog: MutableRefObject<HTMLDialogElement | null>;
  available: boolean;
  resetBtn: MutableRefObject<null>;
  setAvailable: Dispatch<SetStateAction<boolean>>;
  loaded: boolean;
}

export const ProductSearchBoxContext =
  createContext<IProductSearchBoxCtx | null>(null);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function ProductSearchBoxProvider({ children }: { children: any }) {
  const [available, setAvailable] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const handleKeydown = (event: KeyboardEvent) => {
      if (event.key === 'k' && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        open();
      }
    };

    document.addEventListener('keydown', handleKeydown);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const modal = useRef(null);
  const dialog = useRef<HTMLDialogElement>(null);
  const resetBtn = useRef(null);

  function focus() {
    if (!(dialog?.current as unknown as HTMLDialogElement)?.open) return;

    const { current } = modal;

    if (!current) return;

    const input = (current as HTMLFormElement).querySelector('input');

    input?.focus();
  }

  function reset() {
    if (!(dialog?.current as unknown as HTMLDialogElement)?.open) return;

    const { current } = modal;

    if (!current) return;

    (current as HTMLFormElement).reset();
  }

  function open() {
    setLoaded(true);
    const { current } = dialog;

    if (!current) return;

    if ((current as HTMLDialogElement).open) return;

    (current as HTMLDialogElement).showModal();
  }

  function close() {
    const { current } = dialog;

    if (!current) return;

    if (!(current as HTMLDialogElement).open) return;

    (current as HTMLDialogElement).close();
  }

  const value: IProductSearchBoxCtx = {
    open,
    close,
    focus,
    reset,
    modal,
    dialog,
    resetBtn,
    available,
    setAvailable,
    loaded,
  };

  return (
    <ProductSearchBoxContext.Provider value={value}>
      {children}
    </ProductSearchBoxContext.Provider>
  );
}
